import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Calendar, ChevronLeft, RefreshCw, Printer, Download, Search, AlertTriangle,
  ChevronRight as ArrowRight,
} from 'lucide-react';
import { vouchersApi, BalanceSheetReport } from '../services/api';
import { useToast } from '../components/Toast/Toast';
import useReportKeyboardNav from '../hooks/useReportKeyboardNav';
import useDrillPath from '../hooks/useDrillPath';
import useDrillFocus from '../hooks/useDrillFocus';
import useChangePeriodKey from '../hooks/useChangePeriodKey';

/**
 * Balance Sheet in Tally's two-column layout.
 *
 *   Liabilities                    | Assets
 *     Capital Account              |   Fixed Assets
 *     Current Liabilities          |   Current Assets
 *     Profit & Loss A/c            |   Investments
 *       Opening Balance            |   …
 *       Current Period             |
 *     Difference in opening bal.   |
 *     ---- Total ---- both sides equal ----
 *
 * Every group sits on the side its balance actually falls, exactly as Tally
 * does — a liability group carrying a debit balance moves to the Assets column
 * rather than printing as a negative. Revenue and expense groups don't appear
 * at all; they're represented by the single Profit & Loss A/c line, split into
 * the balance brought forward and the current period's result.
 *
 * The two sides agree because the closing balances of every account sum to
 * zero. Anything that doesn't balance is the opening-balance gap, which gets
 * its own line rather than being quietly absorbed.
 */

const fmt = (n: any) =>
  Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const toInputDate = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const displayDate = (s?: string | null) => {
  if (!s) return '—';
  const d = new Date(s);
  return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
};
function fyBounds(d: Date) {
  const y = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return { from: new Date(y, 3, 1), to: new Date(y + 1, 2, 31) };
}

const STORAGE_KEY = 'balance-sheet-filters';

/** A line on one side. `sub` renders Tally's indented italic detail rows. */
type Line = {
  label: string;
  /** Already flipped positive for the side it sits on. */
  amount: number;
  groupId?: number;
  /** Set for the Profit & Loss A/c line. */
  pl?: boolean;
  /** Set for the opening-difference line — informational, not drillable. */
  diff?: boolean;
  sub?: Array<{ label: string; amount: number }>;
  /** Position in the keyboard walk over the statement's openable lines. */
  navIndex?: number;
  /** 0 = Liabilities column, 1 = Assets — ← / → move between them. */
  navCol?: 0 | 1;
  /** Visual row within its column, so ← / → land alongside. */
  navRow?: number;
};

type Crumb = { id: number; name: string; fromIndex?: number };
// fromIndex = the cursor row, at the PARENT level, that opened this crumb.
// It lives on the crumb (and so in the URL) rather than in a ref, because a
// ref is wiped when opening a ledger unmounts the page — which is why coming
// back and then stepping up a level used to land the cursor on the first row.

type DrillRow = {
  type: 'group' | 'ledger';
  id: number;
  name: string;
  opening: number;
  debit: number;
  credit: number;
  closing: number;
};

export default function BalanceSheet() {
  const navigate = useNavigate();
  const { showError } = useToast();
  const [searchParams] = useSearchParams();

  const initial = (() => {
    const today = new Date();
    const { from } = fyBounds(today);
    const fallback = { dateFrom: toInputDate(from), dateTo: toInputDate(today) };
    const urlFrom = searchParams.get('date_from');
    const urlTo = searchParams.get('date_to');
    if (urlFrom || urlTo) {
      return { dateFrom: urlFrom || fallback.dateFrom, dateTo: urlTo || fallback.dateTo };
    }
    try { const s = localStorage.getItem(STORAGE_KEY); if (s) return { ...fallback, ...JSON.parse(s) }; }
    catch { /* ignore */ }
    return fallback;
  })();

  const [dateFrom, setDateFrom] = useState(initial.dateFrom);
  const [dateTo, setDateTo]     = useState(initial.dateTo);
  const [showControls, setShowControls] = useState(false);
  // Alt+F2 — Tally's Change Period: open the date filter, cursor in From.
  useChangePeriodKey(() => setShowControls(true));
  const [loading, setLoading]   = useState(false);
  const [printing, setPrinting] = useState(false);
  const [data, setData]         = useState<BalanceSheetReport | null>(null);

  // Mirrored into the URL so drilling out to a ledger and pressing Back returns
  // to THIS depth instead of the report root.
  const [crumbs, setCrumbs] = useDrillPath<Crumb>();
  const { remember: rememberFocus, take: takeFocus } = useDrillFocus('balance-sheet');
  const [drillRows, setDrillRows] = useState<DrillRow[]>([]);
  const [drillLoading, setDrillLoading] = useState(false);
  const current = crumbs.length ? crumbs[crumbs.length - 1] : null;

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ dateFrom, dateTo })); } catch { /* ignore */ }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    const onAfter = () => setPrinting(false);
    window.addEventListener('afterprint', onAfter);
    return () => window.removeEventListener('afterprint', onAfter);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await vouchersApi.getBalanceSheet({
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      });
      if (res.success) setData(res.data);
    } catch { showError('Error', 'Failed to load the Balance Sheet'); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo, showError]);

  useEffect(() => { load(); }, [load]);

  // Drill-down shows closing balances — a balance sheet is a position, not a
  // movement, so the breakdown is measured the same way.
  useEffect(() => {
    if (!current) { setDrillRows([]); return; }
    let cancelled = false;
    setDrillLoading(true);
    vouchersApi.getGroupLedgers(current.id, {
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
    })
      .then(res => {
        if (cancelled || !res.success) return;
        setDrillRows((res.data.rows || []).map((r: any) => ({
          type: r.row_type === 'subgroup' ? 'group' : 'ledger',
          id: r.row_type === 'subgroup' ? r.group_id : r.ledger_id,
          name: r.ledger_name,
          opening: Number(r.opening_balance) || 0,
          debit: Number(r.debit_total) || 0,
          credit: Number(r.credit_total) || 0,
          closing: Number(r.closing_balance) || 0,
        })));
      })
      .catch(() => { if (!cancelled) showError('Error', 'Failed to load the breakdown'); })
      .finally(() => { if (!cancelled) setDrillLoading(false); });
    return () => { cancelled = true; };
  }, [current, dateFrom, dateTo, showError]);

  // ── Split the groups across the two columns by the sign of their balance ──
  const liabilities: Line[] = [];
  const assets: Line[] = [];
  let liabTotal = 0, assetTotal = 0;

  if (data) {
    for (const r of data.rows) {
      // Zero-balance groups are omitted. Without a nature column there is no
      // reliable side to print them on, and guessing wrong reads worse than
      // leaving them out.
      if (Math.abs(r.closing) <= 0.005) continue;
      if (r.closing < 0) liabilities.push({ label: r.group_name, amount: -r.closing, groupId: r.group_id });
      else assets.push({ label: r.group_name, amount: r.closing, groupId: r.group_id });
    }

    if (data.profit_and_loss) {
      const pl = data.profit_and_loss;
      // Sub-lines carry the sign of their effect on the side the line sits on,
      // so a loss for the period prints as (-) beneath an accumulated profit.
      const sub = [
        { label: 'Opening Balance', amount: pl.closing < 0 ? -pl.opening : pl.opening },
        { label: 'Current Period',  amount: pl.closing < 0 ? -pl.current_period : pl.current_period },
      ];
      const line: Line = { label: 'Profit & Loss A/c', amount: Math.abs(pl.closing), pl: true, sub };
      if (Math.abs(pl.closing) > 0.005 || Math.abs(pl.current_period) > 0.005) {
        (pl.closing <= 0 ? liabilities : assets).push(line);
      }
    }

    if (Math.abs(data.opening_difference) > 0.005) {
      const line: Line = {
        label: 'Difference in opening balances',
        amount: Math.abs(data.opening_difference),
        diff: true,
      };
      (data.opening_difference < 0 ? liabilities : assets).push(line);
    }

    liabTotal = liabilities.reduce((s, l) => s + l.amount, 0);
    assetTotal = assets.reduce((s, l) => s + l.amount, 0);
  }

  // Where the cursor should land when stepping back up a level.
  const pendingRestore = useRef<number | null>(null);

  const openLine = (l: Line, fromIndex?: number) => {
    if (l.pl) {
      const qs = new URLSearchParams();
      if (dateFrom) qs.append('date_from', dateFrom);
      if (dateTo) qs.append('date_to', dateTo);
      navigate(`/reports/profit-loss${qs.toString() ? `?${qs.toString()}` : ''}`);
      return;
    }
    if (l.diff || !l.groupId) return;
    setCrumbs([{ id: l.groupId, name: l.label, fromIndex }]);
  };

  // Number the openable lines down Liabilities and then Assets — that's the
  // order ↑/↓ walk them in — recording the column and visual row so ← / → can
  // jump across. The difference line is informational, so it's skipped.
  const navLines: Line[] = [];
  ([[liabilities, 0], [assets, 1]] as Array<[Line[], 0 | 1]>).forEach(([side, col]) => {
    side.forEach((l, i) => {
      if (l.diff || (!l.groupId && !l.pl)) return;
      l.navIndex = navLines.length;
      l.navCol = col;
      l.navRow = i;
      navLines.push(l);
    });
  });

  const goDeeper = (fromIndex: number, crumb: Crumb) => {
    setCrumbs(prev => [...prev, { ...crumb, fromIndex }]);
  };
  const goBack = () => {
    if (!crumbs.length) return;
    pendingRestore.current = crumbs[crumbs.length - 1]?.fromIndex ?? 0;
    setCrumbs(prev => prev.slice(0, -1));
  };

  // Opening a ledger leaves this route entirely, so note which row we left from
  // — coming back restores the depth (via the URL) AND lands the cursor here.
  const openLedger = (id: number, index: number) => {
    rememberFocus(JSON.stringify(crumbs), id, index);
    navigate(`/reports/ledger?ledger_id=${id}`);
  };

  // Keyboard navigation: ↑/↓ move, ←/→ switch column, Enter opens,
  // Esc/Backspace goes back up the breadcrumb.
  const scrollRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef(-1);
  const { activeIndex, focusIndex, rowProps, rowClass } = useReportKeyboardNav({
    rowCount: current ? drillRows.length : navLines.length,
    containerRef: scrollRef,
    enabled: !showControls && !printing,
    onActivate: i => {
      if (!current) { const l = navLines[i]; if (l) openLine(l, i); return; }
      const r = drillRows[i];
      if (!r) return;
      if (r.type === 'group') goDeeper(i, { id: r.id, name: r.name });
      else openLedger(r.id, i);
    },
    onBack: goBack,
    onHorizontal: dir => {
      if (current) return false;             // drill view: let it scroll instead
      const from = navLines[activeRef.current];
      if (!from) { if (navLines.length) focusIndex(0); return true; }
      const targetCol = dir < 0 ? 0 : 1;
      if (from.navCol === targetCol) return true;
      const candidates = navLines.filter(l => l.navCol === targetCol);
      if (!candidates.length) return true;
      const best = candidates.reduce((a, b) =>
        Math.abs((b.navRow ?? 0) - (from.navRow ?? 0)) < Math.abs((a.navRow ?? 0) - (from.navRow ?? 0)) ? b : a);
      focusIndex(best.navIndex!);
      return true;
    },
  });

  useEffect(() => { activeRef.current = activeIndex; }, [activeIndex]);

  // Put the cursor back once the level we returned to has its rows. Two sources:
  // an in-page step back up the breadcrumb (pendingRestore), or a return from
  // the ledger route (the focus memo). The memo is matched by ledger ID first
  // so the cursor lands on the same ledger even if the rows have since shifted;
  // the saved index is only the fallback for a row that no longer exists.
  useEffect(() => {
    const count = current ? drillRows.length : navLines.length;
    if (count <= 0) return;

    if (pendingRestore.current === null) {
      const memo = takeFocus(JSON.stringify(crumbs));
      if (memo) {
        const byId = drillRows.findIndex(r => r.type === 'ledger' && r.id === memo.id);
        focusIndex(byId >= 0 ? byId : Math.min(memo.index, count - 1));
      }
      return;
    }

    const target = Math.min(pendingRestore.current, count - 1);
    pendingRestore.current = null;
    focusIndex(target);
  }, [current, drillRows, navLines.length, focusIndex, crumbs, takeFocus]);

  const handlePrint = () => { setPrinting(true); setTimeout(() => window.print(), 50); };

  const handleExcel = () => {
    if (!data) return;
    const rows: string[][] = [
      ['Balance Sheet', `as at ${displayDate(dateTo)}`],
      [],
      ['Liabilities', 'Amount', 'Assets', 'Amount'],
    ];
    for (let i = 0; i < Math.max(liabilities.length, assets.length); i++) {
      rows.push([
        liabilities[i]?.label || '', liabilities[i] ? fmt(liabilities[i].amount) : '',
        assets[i]?.label || '', assets[i] ? fmt(assets[i].amount) : '',
      ]);
      for (const s of liabilities[i]?.sub || []) rows.push([`   ${s.label}`, fmt(s.amount), '', '']);
      for (const s of assets[i]?.sub || []) rows.push(['', '', `   ${s.label}`, fmt(s.amount)]);
    }
    rows.push(['Total', fmt(liabTotal), 'Total', fmt(assetTotal)]);
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Balance_Sheet_as_at_${dateTo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const cellL = 'px-3 py-1.5 text-[13px] leading-snug';
  const cellR = 'px-3 py-1.5 text-[13px] leading-snug text-right tabular-nums';

  /** One column, padded so both sides line up row-for-row. */
  const Column = ({ lines, rows }: { lines: Line[]; rows: number }) => (
    <table className="w-full border-collapse">
      <tbody>
        {Array.from({ length: rows }).map((_, i) => {
          const l = lines[i];
          const clickable = !!l && !l.diff && (!!l.groupId || !!l.pl) && !printing;
          return (
            <Fragment key={i}>
              <tr
                {...(l?.navIndex !== undefined ? rowProps(l.navIndex) : {})}
                onClick={clickable ? () => openLine(l, l.navIndex) : undefined}
                title={clickable
                  ? (l.pl ? 'Open the Profit & Loss A/c' : `Break down ${l.label}`)
                  : (l?.diff ? "Opening balances on the ledger masters don't net to zero. Correct them and this line disappears." : undefined)}
                className={`${clickable ? 'cursor-pointer hover:bg-blue-50 transition-colors' : ''} ${
                  l?.navIndex !== undefined ? rowClass(l.navIndex) : ''}`}>
                <td className={`${cellL} font-bold ${l?.diff ? 'text-amber-700' : clickable ? 'text-blue-700' : 'text-slate-800'}`}>
                  {l?.label || ' '}
                </td>
                <td className={`${cellR} font-semibold ${l?.diff ? 'text-amber-700' : 'text-slate-800'}`}>
                  {l && Math.abs(l.amount) > 0.005 ? fmt(l.amount) : ''}
                </td>
              </tr>
              {(l?.sub || []).map((s, si) => (
                <tr key={`${i}-sub-${si}`}>
                  <td className={`${cellL} pl-8 italic text-slate-500`}>{s.label}</td>
                  <td className={`${cellR} italic text-slate-500`}>
                    {s.amount < 0 ? `(-)${fmt(Math.abs(s.amount))}` : fmt(s.amount)}
                  </td>
                </tr>
              ))}
            </Fragment>
          );
        })}
      </tbody>
    </table>
  );

  const liabRows = liabilities.length + liabilities.reduce((s, l) => s + (l.sub?.length || 0), 0);
  const assetRows = assets.length + assets.reduce((s, l) => s + (l.sub?.length || 0), 0);
  const padRows = Math.max(liabilities.length, assets.length);

  return (
    <div className="flex flex-col w-full fixed left-0 right-0 top-14 bottom-16 sm:static sm:h-full" style={{ overscrollBehavior: 'contain' }}>

      {/* Header */}
      <div className="flex-none bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between print:hidden">
        <div className="flex items-center gap-1 min-w-0 flex-1">
          <button
            onClick={() => crumbs.length ? goBack() : navigate(-1)}
            className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 -ml-1 flex-shrink-0">
            <ChevronLeft size={20} />
          </button>
          <div className="flex items-center gap-1 min-w-0 flex-wrap">
            <button
              onClick={() => { if (crumbs.length) { pendingRestore.current = crumbs[0]?.fromIndex ?? 0; setCrumbs([]); } }}
              className={`text-[15px] font-bold truncate ${crumbs.length ? 'text-blue-600 hover:underline' : 'text-slate-800'}`}>
              Balance Sheet
            </button>
            {crumbs.map((c, i) => (
              <span key={`${c.id}-${i}`} className="flex items-center gap-1 min-w-0">
                <ArrowRight size={13} className="text-slate-400 flex-shrink-0" />
                <button
                  onClick={() => setCrumbs(prev => prev.slice(0, i + 1))}
                  className={`text-[14px] font-semibold truncate max-w-[160px] ${
                    i === crumbs.length - 1 ? 'text-slate-800' : 'text-blue-600 hover:underline'
                  }`}>
                  {c.name}
                </button>
              </span>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button onClick={() => setShowControls(v => !v)}
            title="Period & filters (Alt+F2)"
            className={`p-2 rounded-lg transition-colors ${showControls ? 'bg-blue-600 text-white' : 'text-slate-500 hover:bg-slate-100'}`}>
            <Search size={18} />
          </button>
          <button onClick={handleExcel} disabled={!data} title="Export to Excel"
            className="p-2 rounded-lg text-green-600 hover:bg-green-50 disabled:opacity-40">
            <Download size={16} />
          </button>
          <button onClick={handlePrint} disabled={!data}
            className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 disabled:opacity-40">
            <Printer size={16} />
          </button>
          <button onClick={load} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Filters */}
      {showControls && (
        <div className="flex-none bg-slate-50 border-b border-slate-200 px-3 py-2.5 print:hidden">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-stretch border border-slate-300 rounded-lg bg-white overflow-hidden">
              <div className="flex items-center gap-1 px-2 border-r border-slate-300 bg-slate-100">
                <Calendar size={12} className="text-slate-400" />
                <span className="text-[10px] text-slate-500 uppercase font-bold tracking-wide">From</span>
              </div>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                className="text-[13px] text-slate-700 outline-none bg-transparent px-2 py-1.5 w-[115px]" />
              <div className="flex items-center px-2 border-l border-r border-slate-300 bg-slate-100">
                <span className="text-[10px] text-slate-500 uppercase font-bold tracking-wide">As at</span>
              </div>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                className="text-[13px] text-slate-700 outline-none bg-transparent px-2 py-1.5 w-[115px]" />
            </div>
            <span className="text-[11px] text-slate-400">
              Balances are as at the second date; the first only splits the Profit &amp; Loss line
              into brought-forward and current period.
            </span>
          </div>
        </div>
      )}

      {/* ── Drill-down ── */}
      {current ? (
        <div ref={scrollRef} className="flex-1 min-h-0 overflow-auto bg-white">
          <table className="w-full border-collapse">
            <thead className="sticky top-0 z-10">
              <tr>
                {['Particulars', 'Opening', 'Debit', 'Credit', 'Closing'].map((h, i) => (
                  <th key={h} className={`border border-slate-400 bg-slate-200 px-2.5 py-1.5 text-[13px] font-bold text-slate-700 uppercase tracking-wide ${
                    i === 0 ? 'text-left' : 'text-right w-[140px]'}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {drillLoading ? (
                <tr><td colSpan={5} className="text-center text-slate-400 py-16 text-[14px]">Loading…</td></tr>
              ) : drillRows.length === 0 ? (
                <tr><td colSpan={5} className="text-center text-slate-400 py-16 text-[14px]">
                  Nothing under {current.name} as at {displayDate(dateTo)}
                </td></tr>
              ) : drillRows.map((row, rowIdx) => {
                const isGroup = row.type === 'group';
                return (
                  <tr key={`${row.type}-${row.id}`}
                    {...rowProps(rowIdx)}
                    onClick={() => isGroup
                      ? goDeeper(rowIdx, { id: row.id, name: row.name })
                      : openLedger(row.id, rowIdx)}
                    title={isGroup ? `Break down ${row.name}` : `Open the ${row.name} ledger`}
                    className={`bg-white hover:bg-blue-50 cursor-pointer transition-colors ${rowClass(rowIdx)}`}>
                    <td className="border border-slate-300 px-2.5 py-1.5 text-[13px]">
                      <div className="flex items-center gap-2">
                        {isGroup
                          ? <ArrowRight size={14} className="text-blue-400 flex-shrink-0" />
                          : <span className="w-3 h-3 rounded-full border border-slate-300 flex-shrink-0" />}
                        <span className={isGroup ? 'font-semibold text-slate-800' : 'text-slate-700'}>{row.name}</span>
                      </div>
                    </td>
                    <td className="border border-slate-300 px-2.5 py-1.5 text-[13px] text-right tabular-nums text-slate-600">
                      {Math.abs(row.opening) > 0.005
                        ? <>{fmt(Math.abs(row.opening))}<span className="text-[10px] text-slate-400 ml-1">{row.opening > 0 ? 'Dr' : 'Cr'}</span></>
                        : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="border border-slate-300 px-2.5 py-1.5 text-[13px] text-right tabular-nums text-emerald-700">
                      {row.debit > 0.005 ? fmt(row.debit) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="border border-slate-300 px-2.5 py-1.5 text-[13px] text-right tabular-nums text-red-600">
                      {row.credit > 0.005 ? fmt(row.credit) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className={`border border-slate-300 px-2.5 py-1.5 text-[13px] text-right tabular-nums font-semibold ${
                      row.closing > 0 ? 'text-emerald-700' : row.closing < 0 ? 'text-red-600' : 'text-slate-400'}`}>
                      {Math.abs(row.closing) > 0.005
                        ? <>{fmt(Math.abs(row.closing))}<span className="text-[10px] ml-1">{row.closing > 0 ? 'Dr' : 'Cr'}</span></>
                        : <span className="text-slate-300">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {!drillLoading && drillRows.length > 0 && (
              <tfoot>
                <tr className="bg-slate-200 font-bold sticky bottom-0">
                  <td className="border border-slate-400 px-2.5 py-1.5 text-[13px] text-slate-700">Total — {current.name}</td>
                  <td className="border border-slate-400 px-2.5 py-1.5 text-right text-[13px] tabular-nums text-slate-700">
                    {fmt(Math.abs(drillRows.reduce((s, r) => s + r.opening, 0)))}
                  </td>
                  <td className="border border-slate-400 px-2.5 py-1.5 text-right text-[13px] tabular-nums text-emerald-700">
                    {fmt(drillRows.reduce((s, r) => s + r.debit, 0))}
                  </td>
                  <td className="border border-slate-400 px-2.5 py-1.5 text-right text-[13px] tabular-nums text-red-600">
                    {fmt(drillRows.reduce((s, r) => s + r.credit, 0))}
                  </td>
                  <td className="border border-slate-400 px-2.5 py-1.5 text-right text-[13px] tabular-nums text-slate-900">
                    {fmt(Math.abs(drillRows.reduce((s, r) => s + r.closing, 0)))}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      ) : (
        // Same ref as the drill view — only one of the two branches is ever
        // mounted, and the statement needs it so the arrow keys can scroll it.
        <div ref={scrollRef} className="flex-1 min-h-0 overflow-auto bg-white">
          {loading ? (
            <div className="py-20 text-center text-slate-400 text-sm">Loading…</div>
          ) : !data?.profit_and_loss ? (
            <div className="py-20 text-center text-slate-400 text-sm px-8">
              The Balance Sheet is a company-wide statement, so it isn't available to users
              restricted to a ledger group.
            </div>
          ) : (
            <>
              <div className="border border-slate-300 m-3 rounded-lg overflow-hidden">
                <div className="grid grid-cols-2 divide-x divide-slate-300 bg-slate-100 border-b border-slate-300">
                  {['Liabilities', 'Assets'].map(h => (
                    <div key={h} className="flex items-baseline justify-between px-3 py-2">
                      <span className="text-[13px] font-bold tracking-[0.2em] text-slate-700">{h}</span>
                      <span className="text-[11px] text-slate-500">as at {displayDate(dateTo)}</span>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-2 divide-x divide-slate-300">
                  <Column lines={liabilities} rows={padRows} />
                  <Column lines={assets} rows={padRows} />
                </div>

                <div className="grid grid-cols-2 divide-x divide-slate-300 bg-slate-200 border-t border-slate-400">
                  {[liabTotal, assetTotal].map((t, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-2">
                      <span className="text-[13px] font-bold tracking-[0.15em] text-slate-800">Total</span>
                      <span className="text-[13px] font-bold tabular-nums text-slate-900">{fmt(t)}</span>
                    </div>
                  ))}
                </div>
              </div>

              {Math.abs(data.opening_difference) > 0.005 && (
                <div className="mx-4 mb-4 flex items-start gap-1.5 text-[12px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2 print:hidden">
                  <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                  <span>
                    Opening balances on the ledger masters don't net to zero, so{' '}
                    <strong>{fmt(Math.abs(data.opening_difference))}</strong> is shown as its own line
                    to keep both sides equal. Every voucher balances — this is purely the masters'
                    opening figures. Correct them (or post a balancing entry) and the line disappears.
                  </span>
                </div>
              )}

              {liabRows + assetRows === 0 && (
                <div className="py-10 text-center text-slate-400 text-sm">
                  No balances as at {displayDate(dateTo)}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
